import { FindPayments, PaymentMethodTypeEnum, PlatformsEnum } from '@bondsports/types';
import { Icons, SizeEnum } from '@bondsports/utils';
import { ReplaceMethodSteps } from '../components/customers/ManagePaymentMethods/components/ReplacePaymentMethodModal/enums';

export type FindPaymentsWithSearchAndOrder = FindPayments & { search?: string; orderBy?: string };

export interface GetPaymentsResponse {
	meta: {
		totalItems: number;
		itemsPerPage: number;
		totalPages: number;
		currentPage: number;
	};
	data: Payment[];
}

export interface Payment {
	id: number;
	total: number;
	paymentMethod: EPaymentMethod;
	status: PaymentChargeStatusEnum;
	createdAt: Date;
	invoices: number[];
}

//  Matches response from api
export interface IPayment {
	bondFee: number;
	createdAt: string;
	creatingUserId: number;
	currency: string;
	fundLeft: number;
	id: number;
	isRefunded: boolean;
	organizationId: number;
	payingUserId: number;
	paymentMethodId?: string;
	paymentProcessorId?: string;
	paymentProcessorTransactionId?: string;
	paymentStatus: PaymentChargeStatusEnum;
	paymentType: EPaymentMethod;
	platform: string;
	price: number;
	receiptUrl?: string;
	shiftId?: number;
	stripeFee: number;
	updatedAt: string;
	totalPrice?: number;
	splitsNumber?: number;
}

export enum EPaymentMethod {
	ACH = 'us_bank_account',
	BALANCE = 'balance',
	CARD = 'card',
	CARD_ON_TERMINAL = 'card-on-terminal',
	CASH = 'cash',
	CHECK = 'check',
	MIGRATED = 'migrated',
	OTHER = 'other',
	SCHEDULED_CHARGE = 'scheduled-charge',
	GIFT_CARD = 'gift-card',
}

export enum EChargeInnerStep {
	SWIPE = 'swipe',
	CARD_ON_TERMINAL_SETUP_INTENT = 'card-on-terminal-setup-intent',
	CHOOSE_PAYMENT_METHOD_TYPE = 'choose-type',
	NEW_CARD = 'newCard',
	SCHEDULED_CHARGE = 'scheduled-charge',
	SCHEDULED = 'scheduled',
	RESCHEDULE = 'reschedule',
}

export enum EChargeStatus {
	COMPLETED = 'completed',
	ERROR = 'error',
}

export type TChargeSteps = EChargeStatus | EChargeInnerStep | PaymentMethodTypeEnum | ReplaceMethodSteps | '';

export enum EPaymentStatus {
	NOT_PAID = 'not_paid',
	FULLY_PAID = 'paid',
	PARTIAL_PAYMENT = 'partial',
	PURCHASE_ORDER = 'purchase_order',
	REFUNDED = 'refunded',
	SCHEDULED = 'scheduled',
	VOID = 'void',
	QUOTE = 'quote',
}

export enum EPaymentStatusInvoiceFilter {
	NOT_PAID = 'not_paid',
	PAID = 'paid',
	PARTIAL = 'partial',
	REFUNDED = 'refunded',
	VOID = 'void',
	PENDING = 'pending',
	DISPUTED = 'disputed',
	CHARGEBACK = 'chargeback',
}

export enum PaymentChargeStatusEnum {
	paid = 'Paid',
	refunded = 'Refunded',
	pending = 'Pending',
	failed = 'Failed',
	disputed = 'Disputed',
	chargeback = 'Chargeback',
}

export interface IPayment {
	plannedDate: string;
	price: number;
}

export interface PaymentMethod {
	id: string;
	billing_details: {
		address: {
			city: string | null;
			country: string | null;
			line1: string | null;
			line2: string | null;
			postalCode: string | null;
			state: string | null;
		};
		email: string;
		name: string;
		phone: string;
		account_number: string;
	};
	card: {
		brand: string;
		country?: string;
		exp_month: string;
		exp_year: string;
		fingerprint?: string;
		funding?: string;
		generated_from?: string | null;
		last4: string;
	};
	type: PaymentMethodTypeEnum;
}

export interface ICustomerFunds {
	// fundsLeft?: number;
	fundLeft?: number;
	paymentMethodId: string;
	paymentType: PaymentMethodTypeEnum;
	ccBrand?: string;
	ccLast4?: string;
	payingUser?: IPayingUser;
	providerPaymentId?: string;
}
export interface IPaymentMethod {
	type: EPaymentMethod | PaymentMethodTypeEnum | any;
	selected?: boolean;
	id?: number;
	paymentMethodId?: string;
	icon?: Icons;
	primaryText?: string;
	secondaryText?: string;
	amount?: number; // Amount on a single card or ach payment
	refundAmount?: number; // amount to refund on this payment method
	refundSuccess?: boolean;
	receiptId?: number;
	payingUserId?: number;
	user?: IPayingUser;
	providerPaymentId?: string;
	ccBrand?: string;
}
export interface IPopulatedPaymentMethod extends IPaymentMethod {
	payingUser?: IPayingUser;
}

export interface IRefundFeeRequest {
	id: number;
	refundAmount: number;
}

export enum RefundModeEnum {
	REFUND = 'refund',
	REFUND_AND_VOID = 'refund-and-void',
}

export enum PaymentProcessorEnum {
	STRIPE = 'stripe',
	OTHER = 'other',
}
export interface IOrganizationPaymentMethod {
	paymentMethodType: PaymentMethodTypeEnum;
	paymentMethodProcessor: PaymentProcessorEnum;
	minimumAmount: number;
	daysToRefunds?: number;
	platform: PlatformsEnum;
	id: number;
	organizationId: number;
}

export interface IPayingUser {
	firstName?: string;
	lastName?: string;
	email?: string;
	id: number;
}
export interface IInvoiceData {
	isPublic?: boolean;
	owner?: IPayingUser;
}

export interface IDiscountAmountData {
	amount?: number;
	percentage?: number;
}

export interface IDiscountNote {
	content: string;
	isPublic: boolean;
}

export interface IDiscountData extends IDiscountAmountData {
	discountId?: number;
	lineItemsIds: number[];
}

export interface IRemoveDiscount {
	actionId: string;
	lineItemIds?: number[];
}

export interface IRemoveDiscountData {
	discounts: IRemoveDiscount[];
	shiftId?: number;
}

export interface CheckboxProps {
	isDisabled: boolean;
	checked: boolean;
	action: (val: boolean) => void;
	label: string;
	sizer?: SizeEnum;
}

export interface IRefundPaymentMethods {
	type: PaymentMethodTypeEnum;
	paymentMethodId: string;
	icon?: string;
	primaryText?: string;
	amount: number;
	payingUserId?: number;
	payingUser?: IPayingUser;
}
